<script setup>
// SidebarPanel — painel principal da sidebar (à direita do rail de ícones).
// Recebe um item top-level (Conversation, Captain, etc) e renderiza sua
// sub-árvore como flat tree com expand/collapse persistido.
//
// Virtual scroll via @tanstack/vue-virtual quando o número de nós visíveis
// excede VIRTUAL_THRESHOLD — abaixo disso o ganho de perf não compensa o
// overhead de positioning absoluto.
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useVirtualizer } from '@tanstack/vue-virtual';
import { useSidebarContext } from './provider';
import { useSidebarTree } from './useSidebarTree';
import SidebarTreeNode from './SidebarTreeNode.vue';

const props = defineProps({
  item: { type: Object, required: true },
});

defineEmits(['action']);

// Threshold pra ativar virtualização. Abaixo de 30 nós a lista renderiza
// em menos de 1ms; o overhead de absolute positioning + getVirtualItems
// só vale acima desse ponto.
const VIRTUAL_THRESHOLD = 30;
// Altura aproximada de cada row: h-8 (32px) + py-0.5 wrapper (4px).
// Pequenas variações são absorvidas pelo overscan; não precisa ser exato.
const ROW_HEIGHT = 36;

const route = useRoute();
const { isAllowed, resolvePath } = useSidebarContext();

// Filtragem de permissão recursiva — feita antes do flatten pra manter o
// composable agnóstico de auth.
const filterAllowed = items =>
  (items || [])
    .map(item => {
      if (item.children) {
        const allowedChildren = filterAllowed(item.children);
        // Esconde groups completamente vazios — exceto quando o header
        // tem action (ex: "+ Nova pasta" quando ainda nao existem pastas).
        if (allowedChildren.length === 0 && !item.actions?.length) return null;
        return { ...item, children: allowedChildren };
      }
      return item.to && isAllowed(item.to) ? item : null;
    })
    .filter(Boolean);

const allowedTree = computed(() => filterAllowed(props.item.children || []));

const { visibleNodes, isExpanded, toggle, expandPathTo } = useSidebarTree(
  allowedTree,
  { storageKey: `sidebar-expanded:${props.item.name}`, defaultExpanded: true }
);

const matchesRoute = node => {
  if (!node.to) return false;
  if (route.path === resolvePath(node.to)) return true;
  return node.activeOn?.includes(route.name) ?? false;
};

const activeNodeId = computed(() => {
  const match = visibleNodes.value.find(matchesRoute);
  return match?.id ?? null;
});

// Quando a rota muda, garante que o ancestral do active esteja expandido
// (auto-reveal). useStorage do useSidebarTree mantém o estado entre reloads.
watch(
  () => route.fullPath,
  () => {
    if (activeNodeId.value) expandPathTo(activeNodeId.value);
  },
  { immediate: true }
);

// ===========================================================
// Virtual scroll
// ===========================================================
const scrollParent = ref(null);
const shouldVirtualize = computed(
  () => visibleNodes.value.length > VIRTUAL_THRESHOLD
);

// useVirtualizer espera options reativas — passar getter computed garante
// que count atualize quando visibleNodes muda.
const rowVirtualizer = useVirtualizer(
  computed(() => ({
    count: visibleNodes.value.length,
    getScrollElement: () => scrollParent.value,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  }))
);

const virtualItems = computed(() =>
  shouldVirtualize.value ? rowVirtualizer.value.getVirtualItems() : []
);

const totalSize = computed(() =>
  shouldVirtualize.value ? rowVirtualizer.value.getTotalSize() : 0
);
</script>

<template>
  <section
    class="flex flex-col py-3 overflow-hidden w-52 basis-52 flex-shrink-0 border-l border-n-weak/60"
  >
    <header class="px-4 pb-3 mb-1 border-b border-n-weak/60">
      <h2 class="text-sm font-semibold leading-tight truncate text-n-slate-12">
        {{ item.label }}
      </h2>
    </header>

    <div
      ref="scrollParent"
      class="flex-1 px-2 pt-2 overflow-y-auto no-scrollbar"
    >
      <!-- Virtualizado: divs absolutos sobre um container do tamanho total -->
      <div
        v-if="shouldVirtualize"
        :style="{ height: `${totalSize}px` }"
        class="relative w-full"
        role="list"
      >
        <div
          v-for="virtualRow in virtualItems"
          :key="visibleNodes[virtualRow.index].id"
          :style="{ transform: `translateY(${virtualRow.start}px)` }"
          class="absolute top-0 left-0 w-full"
          role="listitem"
        >
          <SidebarTreeNode
            :node="visibleNodes[virtualRow.index]"
            :active="activeNodeId === visibleNodes[virtualRow.index].id"
            :is-expanded="isExpanded(visibleNodes[virtualRow.index].id)"
            @toggle="toggle(visibleNodes[virtualRow.index].id)"
            @action="$emit('action', $event)"
          />
        </div>
      </div>

      <!-- Render normal: ul/li sem positioning -->
      <ul v-else class="flex flex-col gap-0.5 m-0 list-none reset-base">
        <SidebarTreeNode
          v-for="node in visibleNodes"
          :key="node.id"
          :node="node"
          :active="activeNodeId === node.id"
          :is-expanded="isExpanded(node.id)"
          @toggle="toggle(node.id)"
          @action="$emit('action', $event)"
        />
      </ul>
    </div>
  </section>
</template>
